/*!
 * RBF Cosmetics — Motion Foundation
 * ------------------------------------------------------------------
 * Reusable GSAP animation utilities ONLY. No page or section is
 * animated by this file — future section-building phases call these
 * utilities from their own scripts.
 *
 * LOADING MODEL: this is a classic (non-module) script, loaded via
 * <script defer> AFTER assets/gsap.js and assets/ScrollTrigger.js
 * (both existing UMD bundles already in the theme, previously only
 * enqueued on one section — see build log). It attaches a single
 * global, window.rbfMotion, matching the Phase 2 naming convention
 * ("GSAP: animation timelines and triggers are namespaced in JS as
 * rbfMotion.[interactionName]"). Feature modules (rbf-nav.js,
 * rbf-cart.js, etc.) are ES modules and read window.rbfMotion rather
 * than importing GSAP a second time — this avoids ever bundling GSAP
 * twice and keeps exactly one place in the codebase that talks to
 * the GSAP global directly.
 *
 * Every function here:
 *   - checks prefers-reduced-motion and no-ops to the instant end
 *     state if the user has requested reduced motion
 *   - animates only transform/opacity/height (GSAP-driven, not CSS
 *     max-height hacks) to avoid layout thrash
 *   - never blocks interactivity (no animation disables pointer
 *     events on the element being animated, except drawer/modal
 *     scrims, which is the intended behaviour)
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (typeof window.gsap === 'undefined') {
    // Fail safe and silent: if GSAP didn't load (network issue, blocked
    // resource), the site must still be fully usable — every consumer
    // of rbfMotion checks for its existence before calling it.
    console.warn('[rbf-motion] GSAP not found — motion layer disabled, site remains fully functional.');
    return;
  }

  var gsap = window.gsap;
  var hasScrollTrigger = typeof window.ScrollTrigger !== 'undefined';
  if (hasScrollTrigger) {
    gsap.registerPlugin(window.ScrollTrigger);
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Reads --rbf-duration-* / --rbf-ease-* tokens from rbf-tokens.css so
  // JS and CSS motion timing can never silently drift apart.
  var rootStyles = getComputedStyle(document.documentElement);
  function token(name, fallback) {
    var value = rootStyles.getPropertyValue(name).trim();
    return value || fallback;
  }
  var DURATION = {
    fast: parseFloat(token('--rbf-duration-fast', '150')) / 1000,
    base: parseFloat(token('--rbf-duration-base', '200')) / 1000,
    moderate: parseFloat(token('--rbf-duration-moderate', '300')) / 1000,
    slow: parseFloat(token('--rbf-duration-slow', '500')) / 1000,
    slower: parseFloat(token('--rbf-duration-slower', '600')) / 1000
  };
  var EASE_OUT = 'power2.out';

  /**
   * Wraps a GSAP call so that under reduced motion, the element jumps
   * straight to its target state with no tween, no stagger, no delay.
   */
  function safeAnimate(targets, toVars, fallbackVars) {
    if (prefersReducedMotion()) {
      gsap.set(targets, fallbackVars || toVars);
      return gsap.timeline(); // return an empty, already-complete timeline for a consistent API
    }
    return gsap.to(targets, toVars);
  }

  var rbfMotion = {
    durations: DURATION,
    prefersReducedMotion: prefersReducedMotion,

    /** Fade + rise from below (default reveal for text blocks, cards, images) */
    fadeUp: function (target, opts) {
      opts = opts || {};
      var distance = opts.distance || 12;
      gsap.set(target, { opacity: 0, y: distance });
      return safeAnimate(target, {
        opacity: 1,
        y: 0,
        duration: opts.duration || DURATION.slow,
        ease: opts.ease || EASE_OUT,
        delay: opts.delay || 0
      }, { opacity: 1, y: 0 });
    },

    /** Plain opacity fade — used where a rise would compete with a parent reveal */
    fadeIn: function (target, opts) {
      opts = opts || {};
      gsap.set(target, { opacity: 0 });
      return safeAnimate(target, {
        opacity: 1,
        duration: opts.duration || DURATION.base,
        ease: 'sine.out',
        delay: opts.delay || 0
      }, { opacity: 1 });
    },

    /** Line-by-line text reveal. Expects target's text already split into
     * elements carrying [data-rbf-line] (split performed by the calling
     * section script — this utility never mutates markup itself, keeping
     * DOM structure decisions with the component that owns the content). */
    splitTextReveal: function (containerEl, opts) {
      opts = opts || {};
      var lines = containerEl.querySelectorAll('[data-rbf-line]');
      if (!lines.length) return gsap.timeline();
      gsap.set(lines, { opacity: 0, y: 10 });
      if (prefersReducedMotion()) {
        gsap.set(lines, { opacity: 1, y: 0 });
        return gsap.timeline();
      }
      return gsap.to(lines, {
        opacity: 1,
        y: 0,
        duration: DURATION.slow,
        ease: EASE_OUT,
        stagger: opts.stagger || 0.05
      });
    },

    /** Subtle scale-in, used for image reveals and modal open */
    scaleIn: function (target, opts) {
      opts = opts || {};
      var from = opts.from || 0.96;
      gsap.set(target, { opacity: 0, scale: from });
      return safeAnimate(target, {
        opacity: 1,
        scale: 1,
        duration: opts.duration || DURATION.moderate,
        ease: opts.ease || EASE_OUT
      }, { opacity: 1, scale: 1 });
    },

    /** Light parallax — small translate tied to scroll, capped distance so
     * it never causes noticeable layout/scroll jank. No-ops entirely under
     * reduced motion (parallax is the one effect not worth even an instant
     * fallback state for — it simply doesn't run). */
    lightParallax: function (target, opts) {
      if (!hasScrollTrigger || prefersReducedMotion()) return null;
      opts = opts || {};
      var distance = Math.min(opts.distance || 40, 60); // hard cap: never a jarring shift
      return gsap.to(target, {
        y: distance,
        ease: 'none',
        scrollTrigger: {
          trigger: opts.trigger || target,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        }
      });
    },

    /** Image reveal: fade + 4% scale-down-to-100%, per Design System §12 */
    imageReveal: function (target, opts) {
      opts = opts || {};
      gsap.set(target, { opacity: 0, scale: 1.04 });
      return safeAnimate(target, {
        opacity: 1,
        scale: 1,
        duration: opts.duration || DURATION.slower,
        ease: EASE_OUT
      }, { opacity: 1, scale: 1 });
    },

    /** Card reveal: identical timing contract to imageReveal, kept as a
     * distinct named method so card-grid section scripts read clearly. */
    cardReveal: function (target, opts) {
      return rbfMotion.fadeUp(target, opts);
    },

    /** Staggered reveal across a NodeList/array — grid/list entrance */
    staggerReveal: function (targets, opts) {
      opts = opts || {};
      gsap.set(targets, { opacity: 0, y: opts.distance || 12 });
      if (prefersReducedMotion()) {
        gsap.set(targets, { opacity: 1, y: 0 });
        return gsap.timeline();
      }
      return gsap.to(targets, {
        opacity: 1,
        y: 0,
        duration: DURATION.slow,
        ease: EASE_OUT,
        stagger: opts.stagger != null ? opts.stagger : 0.06
      });
    },

    /** Registers a one-shot ScrollTrigger reveal — the standard way any
     * future section wires "animate once when scrolled into view". Never
     * re-triggers on scroll-back-up, per Design System §12. */
    onScrollReveal: function (target, animateFn, triggerOpts) {
      if (!hasScrollTrigger) {
        animateFn(target); // still animate even if ScrollTrigger failed to load
        return;
      }
      window.ScrollTrigger.create(Object.assign({
        trigger: target,
        start: 'top 85%',
        once: true,
        onEnter: function () { animateFn(target); }
      }, triggerOpts || {}));
    },

    /** Cart drawer / mobile menu slide-in. Returns the open() and close()
     * pair; the calling module owns when to invoke them (focus trapping,
     * scroll-lock, etc. live in rbf-a11y.js and the feature module, not here). */
    drawer: function (drawerEl, scrimEl) {
      return {
        open: function () {
          scrimEl.setAttribute('data-rbf-open', 'true');
          if (prefersReducedMotion()) {
            gsap.set(drawerEl, { x: 0 });
            return;
          }
          gsap.to(drawerEl, { x: 0, duration: DURATION.moderate, ease: EASE_OUT });
        },
        close: function () {
          scrimEl.setAttribute('data-rbf-open', 'false');
          if (prefersReducedMotion()) {
            gsap.set(drawerEl, { x: '100%' });
            return;
          }
          gsap.to(drawerEl, { x: '100%', duration: DURATION.moderate, ease: 'power2.in' });
        }
      };
    },

    /** Modal open/close — scale + fade, matching .rbf-modal's CSS resting state */
    modal: function (modalEl, scrimEl) {
      return {
        open: function () {
          scrimEl.setAttribute('data-rbf-open', 'true');
          rbfMotion.scaleIn(modalEl, { duration: DURATION.moderate });
        },
        close: function () {
          scrimEl.setAttribute('data-rbf-open', 'false');
          if (prefersReducedMotion()) {
            gsap.set(modalEl, { opacity: 0, scale: 0.96 });
            return;
          }
          gsap.to(modalEl, { opacity: 0, scale: 0.96, duration: DURATION.base, ease: 'power2.in' });
        }
      };
    },

    /** Accordion panel expand/collapse via animated height — the GSAP
     * counterpart to the CSS no-JS fallback in rbf-base.css. Measures the
     * panel's natural height once per call (not cached) since content can
     * change (e.g. variant-dependent FAQ text). */
    accordion: {
      open: function (panelEl) {
        panelEl.style.display = 'block';
        panelEl.setAttribute('data-rbf-open', 'true');
        var naturalHeight = panelEl.scrollHeight;
        if (prefersReducedMotion()) {
          gsap.set(panelEl, { height: 'auto' });
          return;
        }
        gsap.fromTo(panelEl, { height: 0 }, {
          height: naturalHeight,
          duration: DURATION.moderate,
          ease: EASE_OUT,
          onComplete: function () { gsap.set(panelEl, { height: 'auto' }); }
        });
      },
      close: function (panelEl) {
        panelEl.setAttribute('data-rbf-open', 'false');
        if (prefersReducedMotion()) {
          panelEl.style.display = 'none';
          return;
        }
        gsap.to(panelEl, {
          height: 0,
          duration: DURATION.base,
          ease: 'power2.in',
          onComplete: function () { panelEl.style.display = 'none'; }
        });
      }
    }
  };

  window.rbfMotion = rbfMotion;
})();
